import { ref, computed } from 'vue'
import { getApiMetrics, resetApiMetrics } from './interceptors/response'

// Interface para métricas detalhadas
export interface DetailedMetrics {
  requestCount: number
  successCount: number
  errorCount: number
  averageResponseTime: number
  lastRequestTime: number
  successRate: number
  errorRate: number
  requestsPerMinute: number
  slowestRequest: number
  fastestRequest: number
}

// Interface para métricas por endpoint
export interface EndpointMetrics {
  endpoint: string
  method: string
  requestCount: number
  successCount: number
  errorCount: number
  averageResponseTime: number
  lastRequestTime: number
}

// Interface para métricas de cache
export interface CacheMetrics {
  hits: number
  misses: number
  hitRate: number
  totalSize: number
  expiredItems: number
}

// Interface para métricas de performance
export interface PerformanceMetrics {
  totalRequests: number
  averageResponseTime: number
  p95ResponseTime: number
  p99ResponseTime: number
  slowestEndpoint: string
  fastestEndpoint: string
  errorRate: number
  uptime: number
}

// Estado global das métricas
const metrics = ref<DetailedMetrics>({
  requestCount: 0,
  successCount: 0,
  errorCount: 0,
  averageResponseTime: 0,
  lastRequestTime: 0,
  successRate: 0,
  errorRate: 0,
  requestsPerMinute: 0,
  slowestRequest: 0,
  fastestRequest: Infinity
})

const endpointMetrics = ref<Map<string, EndpointMetrics>>(new Map())
const cacheMetrics = ref<CacheMetrics>({
  hits: 0,
  misses: 0,
  hitRate: 0,
  totalSize: 0,
  expiredItems: 0
})

const performanceMetrics = ref<PerformanceMetrics>({
  totalRequests: 0,
  averageResponseTime: 0,
  p95ResponseTime: 0,
  p99ResponseTime: 0,
  slowestEndpoint: '',
  fastestEndpoint: '',
  errorRate: 0,
  uptime: 0
})

// Timestamps para cálculos
const startTime = Date.now()
const requestTimes: number[] = []
const responseTimes: number[] = []

class MetricsService {
  /**
   * Atualizar métricas básicas
   */
  updateMetrics(success: boolean, responseTime: number, endpoint?: string) {
    const now = Date.now()
    
    // Métricas gerais
    metrics.value.requestCount++
    metrics.value.lastRequestTime = now
    
    if (success) {
      metrics.value.successCount++
    } else {
      metrics.value.errorCount++
    }
    
    // Atualizar tempo de resposta
    responseTimes.push(responseTime)
    metrics.value.averageResponseTime = 
      responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
    
    // Atualizar slowest/fastest
    if (responseTime > metrics.value.slowestRequest) {
      metrics.value.slowestRequest = responseTime
    }
    if (responseTime < metrics.value.fastestRequest) {
      metrics.value.fastestRequest = responseTime
    }
    
    // Calcular taxas
    metrics.value.successRate = (metrics.value.successCount / metrics.value.requestCount) * 100
    metrics.value.errorRate = (metrics.value.errorCount / metrics.value.requestCount) * 100
    
    // Calcular requests por minuto
    const timeDiff = (now - startTime) / 1000 / 60 // minutos
    metrics.value.requestsPerMinute = metrics.value.requestCount / Math.max(timeDiff, 1)
    
    // Métricas por endpoint
    if (endpoint) {
      this.updateEndpointMetrics(endpoint, success, responseTime)
    }
    
    // Atualizar métricas de performance
    this.updatePerformanceMetrics()
  }

  /**
   * Atualizar métricas por endpoint
   */
  private updateEndpointMetrics(endpoint: string, success: boolean, responseTime: number) {
    const key = endpoint
    const existing = endpointMetrics.value.get(key)
    
    if (existing) {
      existing.requestCount++
      if (success) {
        existing.successCount++
      } else {
        existing.errorCount++
      }
      existing.averageResponseTime = 
        (existing.averageResponseTime + responseTime) / 2
      existing.lastRequestTime = Date.now()
    } else {
      endpointMetrics.value.set(key, {
        endpoint,
        method: 'GET', // Seria extraído da requisição real
        requestCount: 1,
        successCount: success ? 1 : 0,
        errorCount: success ? 0 : 1,
        averageResponseTime: responseTime,
        lastRequestTime: Date.now()
      })
    }
  }

  /**
   * Atualizar métricas de performance
   */
  private updatePerformanceMetrics() {
    performanceMetrics.value.totalRequests = metrics.value.requestCount
    performanceMetrics.value.averageResponseTime = metrics.value.averageResponseTime
    performanceMetrics.value.errorRate = metrics.value.errorRate
    performanceMetrics.value.uptime = Date.now() - startTime
    
    // Calcular percentis
    if (responseTimes.length > 0) {
      const sorted = [...responseTimes].sort((a, b) => a - b)
      const p95Index = Math.floor(sorted.length * 0.95)
      const p99Index = Math.floor(sorted.length * 0.99)
      
      performanceMetrics.value.p95ResponseTime = sorted[p95Index] || 0
      performanceMetrics.value.p99ResponseTime = sorted[p99Index] || 0
    }
    
    // Encontrar endpoints mais lentos/mais rápidos
    let slowest = 0
    let fastest = Infinity
    let slowestEndpoint = ''
    let fastestEndpoint = ''
    
    for (const [key, endpoint] of endpointMetrics.value) {
      if (endpoint.averageResponseTime > slowest) {
        slowest = endpoint.averageResponseTime
        slowestEndpoint = endpoint.endpoint
      }
      if (endpoint.averageResponseTime < fastest) {
        fastest = endpoint.averageResponseTime
        fastestEndpoint = endpoint.endpoint
      }
    }
    
    performanceMetrics.value.slowestEndpoint = slowestEndpoint
    performanceMetrics.value.fastestEndpoint = fastestEndpoint
  }

  /**
   * Atualizar métricas de cache
   */
  updateCacheMetrics(hit: boolean, size?: number) {
    if (hit) {
      cacheMetrics.value.hits++
    } else {
      cacheMetrics.value.misses++
    }
    
    if (size !== undefined) {
      cacheMetrics.value.totalSize = size
    }
    
    const total = cacheMetrics.value.hits + cacheMetrics.value.misses
    cacheMetrics.value.hitRate = total > 0 ? (cacheMetrics.value.hits / total) * 100 : 0
  }

  /**
   * Obter métricas detalhadas
   */
  getDetailedMetrics(): DetailedMetrics {
    return { ...metrics.value }
  }

  /**
   * Obter métricas por endpoint
   */
  getEndpointMetrics(): EndpointMetrics[] {
    return Array.from(endpointMetrics.value.values())
  }

  /**
   * Obter métricas de cache
   */
  getCacheMetrics(): CacheMetrics {
    return { ...cacheMetrics.value }
  }

  /**
   * Obter métricas de performance
   */
  getPerformanceMetrics(): PerformanceMetrics {
    return { ...performanceMetrics.value }
  }

  /**
   * Obter todas as métricas
   */
  getAllMetrics() {
    return {
      detailed: this.getDetailedMetrics(),
      endpoints: this.getEndpointMetrics(),
      cache: this.getCacheMetrics(),
      performance: this.getPerformanceMetrics()
    }
  }

  /**
   * Resetar todas as métricas
   */
  resetMetrics() {
    metrics.value = {
      requestCount: 0,
      successCount: 0,
      errorCount: 0,
      averageResponseTime: 0,
      lastRequestTime: 0,
      successRate: 0,
      errorRate: 0,
      requestsPerMinute: 0,
      slowestRequest: 0,
      fastestRequest: Infinity
    }
    
    endpointMetrics.value.clear()
    cacheMetrics.value = {
      hits: 0,
      misses: 0,
      hitRate: 0,
      totalSize: 0,
      expiredItems: 0
    }
    
    performanceMetrics.value = {
      totalRequests: 0,
      averageResponseTime: 0,
      p95ResponseTime: 0,
      p99ResponseTime: 0,
      slowestEndpoint: '',
      fastestEndpoint: '',
      errorRate: 0,
      uptime: 0
    }
    
    requestTimes.length = 0
    responseTimes.length = 0
  }

  /**
   * Exportar métricas para análise
   */
  exportMetrics() {
    return {
      timestamp: new Date().toISOString(),
      metrics: this.getAllMetrics(),
      system: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        memory: (performance as any).memory ? {
          used: (performance as any).memory.usedJSHeapSize,
          total: (performance as any).memory.totalJSHeapSize,
          limit: (performance as any).memory.jsHeapSizeLimit
        } : null
      }
    }
  }

  /**
   * Verificar saúde da API
   */
  getHealthStatus() {
    const errorRate = metrics.value.errorRate
    const avgResponseTime = metrics.value.averageResponseTime
    
    let status = 'healthy'
    let issues: string[] = []
    
    if (errorRate > 10) {
      status = 'unhealthy'
      issues.push(`High error rate: ${errorRate.toFixed(2)}%`)
    }
    
    if (avgResponseTime > 5000) {
      status = 'degraded'
      issues.push(`Slow response time: ${avgResponseTime.toFixed(2)}ms`)
    }
    
    if (metrics.value.requestsPerMinute > 100) {
      issues.push(`High request rate: ${metrics.value.requestsPerMinute.toFixed(2)} req/min`)
    }
    
    return {
      status,
      issues,
      metrics: this.getDetailedMetrics()
    }
  }
}

// Instância singleton
export const metricsService = new MetricsService()

// Composable para usar métricas em componentes
export function useMetrics() {
  const detailedMetrics = computed(() => metricsService.getDetailedMetrics())
  const endpointMetrics = computed(() => metricsService.getEndpointMetrics())
  const cacheMetrics = computed(() => metricsService.getCacheMetrics())
  const performanceMetrics = computed(() => metricsService.getPerformanceMetrics())
  const healthStatus = computed(() => metricsService.getHealthStatus())
  
  const resetMetrics = () => metricsService.resetMetrics()
  const exportMetrics = () => metricsService.exportMetrics()
  
  return {
    detailedMetrics,
    endpointMetrics,
    cacheMetrics,
    performanceMetrics,
    healthStatus,
    resetMetrics,
    exportMetrics
  }
}

// Função para integrar com interceptors
export function trackApiCall(endpoint: string, success: boolean, responseTime: number) {
  metricsService.updateMetrics(success, responseTime, endpoint)
}

// Função para integrar com cache
export function trackCacheHit(hit: boolean, size?: number) {
  metricsService.updateCacheMetrics(hit, size)
}
